/**
 * Homepage Video Column — click-to-play.
 * Each [data-video-column] holds a poster + play button over either:
 *   - a Shopify <video> (no autoplay), or
 *   - nothing, plus a data-youtube-id for a YouTube video.
 *
 * On play-button click the Shopify video is revealed and played; for YouTube the
 * iframe is injected only at that moment, so no YouTube request or cookie happens
 * on page load. A Shopify video always wins — the section only emits
 * data-youtube-id when no video is uploaded.
 */

const YT_PARAMS = 'autoplay=1&rel=0&modestbranding=1&playsinline=1';

/** Builds the iframe lazily so nothing hits YouTube until the visitor asks. */
const buildYouTubeFrame = (id, title) => {
  const iframe = document.createElement('iframe');
  // youtube-nocookie: no tracking cookie until playback actually starts
  iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?${YT_PARAMS}`;
  iframe.className = 'homepage-video-column__iframe';
  iframe.title = title || 'YouTube video';
  iframe.loading = 'lazy';
  iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
  iframe.allowFullscreen = true;
  iframe.setAttribute('frameborder', '0');
  return iframe;
};

/**
 * With an art-directed pair there are two <video> elements, one per breakpoint —
 * CSS shows exactly one. Pick the visible box's video, not simply the first in the
 * DOM, or a mobile visitor would play the desktop file behind a hidden box.
 */
const visibleVideo = (player) => {
  const boxes = player.querySelectorAll('[data-vc-media]');
  if (!boxes.length) return player.querySelector('video');

  const shown = [...boxes].find((box) => box.offsetParent !== null);
  return (shown || boxes[0]).querySelector('video');
};

/** Seconds -> m:ss, matching the Liquid-side format for Shopify-hosted videos. */
const formatDuration = (seconds) => {
  const total = Math.round(seconds);
  const mm = Math.floor(total / 60);
  const ss = String(total % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

/**
 * Fills the duration label from the file's own metadata. Only runs when Liquid
 * left it empty — i.e. a CDN / direct link, which has no Video object and so no
 * server-side duration. Whichever video reports metadata first wins; the pair are
 * the same content at different aspect ratios.
 */
const fillDurationFromMetadata = (player) => {
  const slot = player.querySelector('[data-video-duration]');
  if (!slot) return;

  const videos = [...player.querySelectorAll('video')];
  if (!videos.length) return;

  const apply = (video) => {
    if (slot.textContent.trim()) return;
    if (!Number.isFinite(video.duration) || video.duration <= 0) return;
    slot.textContent = formatDuration(video.duration);
    slot.hidden = false;
  };

  videos.forEach((video) => {
    // metadata may already be there if the browser got ahead of us
    if (video.readyState >= 1) apply(video);
    video.addEventListener('loadedmetadata', () => apply(video), { once: true });
  });
};

const playShopifyVideo = (video) => {
  video.setAttribute('controls', '');
  const played = video.play();
  if (played && typeof played.catch === 'function') {
    played.catch((error) => {
      console.warn('[video-column] play() blocked:', error);
    });
  }
};

export default function initVideoColumn() {
  const players = document.querySelectorAll('[data-video-column]');

  players.forEach((player) => {
    const button = player.querySelector('[data-video-play]');
    if (!button) return;

    const hasVideo = !!player.querySelector('video');
    const youtubeId = player.dataset.youtubeId;
    if (!hasVideo && !youtubeId) return;

    fillDurationFromMetadata(player);

    const play = () => {
      player.classList.add('is-playing');

      if (hasVideo) {
        // resolved at click time, so a resize between load and play still plays the right file
        const video = visibleVideo(player);
        if (video) playShopifyVideo(video);
        return;
      }

      // guard against a double-click appending two iframes
      if (player.querySelector('.homepage-video-column__iframe')) return;
      const label = player.querySelector('.homepage-video-column__video-title');
      player.appendChild(buildYouTubeFrame(youtubeId, label?.textContent.trim()));
    };

    button.addEventListener('click', play);
  });
}
